import { ShipCard } from "../../../../Cards/ShipCard";
import { FormLabel } from "../../../FormLabel";
import { Input } from "../../../Input";
import { Select } from "../../../Select";
import { useForm } from "react-hook-form";
import { ButtonGroup } from "../../../../Buttons/ButtonGroup";
import { useShipContext } from "../../../../../context/shipContext";
import {
  PREVIOUS_STEP,
  COMPLETE_RECEIVER_DETAILS_STEP,
  SET_AUTOCOMPLETE_ENABLED_TO,
} from "../../../../../reducers/types";
import {
  getZipCodeInputLabel,
  getStatesInputLabel,
  mapCountriesToSelectOptions,
  mapSubdivisionsToSelectOptions,
} from "../../../../../helpers";
import { yupResolver } from "@hookform/resolvers/yup";
import { shipToSchema } from "../../../validations/ship";
import { useGetCountries, useGetSubdivisions } from "../../../../../api/hooks";
import { LoadingLayout } from "../../../../Layouts/LoadingLayout";
import { AddressAutocomplete } from "../../../AddressAutocomplete";
import { AutocompleteToggle } from "../../../AutocompleteToggle";
import { AutocompleteInfoBanner } from "../../../../Alerts/AutocompleteInfoBanner";

export function ShipToForm() {
  const { state: shipState, dispatch } = useShipContext();
  const autocompleteEnabled = shipState.to.autocompleteEnabled;

  const toggleAutocomplete = () => {
    dispatch({
      type: SET_AUTOCOMPLETE_ENABLED_TO,
      payload: !autocompleteEnabled,
    });
  };

  const { control, watch, handleSubmit, setValue } = useForm({
    defaultValues: shipState.to,
    resolver: yupResolver(shipToSchema),
  });

  const { data: countries = [], isLoading: countriesLoading } =
    useGetCountries();

  const countryOptions = mapCountriesToSelectOptions(countries);

  const watchCountry = watch("country");

  const countryId = watchCountry?.id;

  const { data: rawSubdivisions = [], isLoading: subdivisionsLoading } =
    useGetSubdivisions(countryId, !!countryId);

  const provinces = mapSubdivisionsToSelectOptions(rawSubdivisions);

  const goBack = () => {
    dispatch({
      type: PREVIOUS_STEP,
    });
  };

  const onSubmit = (data) => {
    dispatch({
      type: COMPLETE_RECEIVER_DETAILS_STEP,
      payload: data,
    });
  };

  if (countriesLoading || subdivisionsLoading) return <LoadingLayout />;

  return (
    <div data-testid="ship-to-step">
      <ShipCard>
        {autocompleteEnabled && (
          <div className="mb-6">
            <AutocompleteInfoBanner />
          </div>
        )}
        <div className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-12">
          <div className="sm:col-span-12 flex items-center">
            <div className="sm:col-span-3 mr-10">
              <FormLabel title={"Ship To"} />
            </div>
            <div className="sm:col-span-9"></div>
          </div>
          <div className="sm:col-span-9">
            {autocompleteEnabled ? (
              <AddressAutocomplete
                name="address1"
                label="Address"
                control={control}
                countryRestriction={["CA", "US"]}
                addOnRight={
                  <AutocompleteToggle
                    enabled={autocompleteEnabled}
                    onToggle={toggleAutocomplete}
                    tooltipMessage="Uses Google Address Autocomplete. Supported for: Canada, United States. For other countries, please enter the address manually."
                  />
                }
                onSelectAddress={(mappedAddress) => {
                  setValue("address1", mappedAddress.address1, {
                    shouldDirty: true,
                  });
                  setValue("address2", mappedAddress.address2, {
                    shouldDirty: true,
                  });
                  setValue("city", mappedAddress.city, { shouldDirty: true });
                  setValue("state", mappedAddress.state, {
                    shouldDirty: true,
                  });
                  setValue("zip", mappedAddress.zip, { shouldDirty: true });
                  setValue("country", mappedAddress.country, {
                    shouldDirty: true,
                  });
                }}
              />
            ) : (
              <Input
                name="address1"
                label="Address"
                control={control}
                addOnRight={
                  <AutocompleteToggle
                    enabled={autocompleteEnabled}
                    onToggle={toggleAutocomplete}
                    tooltipMessage="Uses Google Address Autocomplete. Supported for: Canada, United States. For other countries, please enter the address manually."
                  />
                }
              />
            )}
          </div>
          <div className="sm:col-span-3">
            <Input
              name="address2"
              label="Apt / Unit / Suite"
              control={control}
              helper="Optional"
            />
          </div>
          <>
            <div className="sm:col-span-3">
              <Select
                name="country"
                label="Country"
                control={control}
                options={countryOptions}
              />
            </div>
            <div className="sm:col-span-3">
              <Input
                name="zip"
                label={getZipCodeInputLabel(countryId)}
                control={control}
              />
            </div>
            <div className="sm:col-span-3">
              <div className="sm:col-span-3">
                <Select
                  name="state"
                  label={getStatesInputLabel(countryId)}
                  control={control}
                  options={provinces}
                />
              </div>
            </div>
            <div className="sm:col-span-3">
              <Input name="city" label="City" control={control} />
            </div>
          </>
          <div className="sm:col-span-4">
            <Input name="name" label="Receiver Name" control={control} />
          </div>
          <div className="sm:col-span-4">
            <Input name="phone" label="Contact No." control={control} />
          </div>
          <div className="sm:col-span-4">
            <Input name="email" label="Email Address" control={control} />
          </div>
        </div>
      </ShipCard>
      <div className="mt-5 sm:mt-5">
        <ButtonGroup
          primaryButtonOnClick={handleSubmit(onSubmit)}
          primaryButtonText="Continue"
          secondaryButtonText="Back"
          secondaryButtonOnClick={goBack}
          primaryTestId="receiver-continue"
          secondaryTestId="receiver-back"
        />
      </div>
    </div>
  );
}
